import React, { useState, useEffect, useRef } from 'react';
import { MessageSquarePlus, ChevronLeft, LogOut, MoreVertical, Share, Edit2, Trash2 } from 'lucide-react';
import { useAuth } from '../context/AuthContext';

// Helper function to format timestamp
const formatTimestamp = (timestamp) => {
    if (!timestamp) return '';
    
    const date = new Date(timestamp);
    const now = new Date();
    const diffMs = now - date;
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);
    
    if (diffMins < 1) return 'Just now';
    if (diffMins < 60) return `${diffMins}m ago`;
    if (diffHours < 24) return `${diffHours}h ago`;
    if (diffDays < 7) return `${diffDays}d ago`;
    
    return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
};

const AgentSidebar = ({ currentAgent, onNavigate, isCollapsed, onToggleCollapse, chatHistory = [], onNewChat, onLoadChat, onRenameChat, onDeleteChat, onShareChat }) => {
    const { logout, user } = useAuth();
    const [openMenuId, setOpenMenuId] = useState(null);
    const [renamingChatId, setRenamingChatId] = useState(null);
    const [renameValue, setRenameValue] = useState('');
    
    // Close menu when clicking outside
    useEffect(() => {
        const handleClickOutside = () => {
            if (openMenuId) setOpenMenuId(null);
        };
        
        document.addEventListener('click', handleClickOutside);
        return () => document.removeEventListener('click', handleClickOutside);
    }, [openMenuId]);
    
    const menuItems = [
        { id: 'new-chat', name: 'New chat', icon: MessageSquarePlus },
    ];

    const handleMenuToggle = (chatId, e) => {
        e.stopPropagation();
        setOpenMenuId(openMenuId === chatId ? null : chatId);
    };

    const handleRenameClick = (chat, e) => {
        e.stopPropagation();
        setRenamingChatId(chat.sessionId);
        setRenameValue(chat.title);
        setOpenMenuId(null);
    };

    const handleRenameSubmit = async (chatId, e) => {
        e.stopPropagation();
        if (renameValue.trim() && onRenameChat) {
            await onRenameChat(chatId, renameValue.trim());
        }
        setRenamingChatId(null);
        setRenameValue('');
    };

    const handleRenameCancel = (e) => {
        e.stopPropagation();
        setRenamingChatId(null);
        setRenameValue('');
    };

    const handleShareClick = async (chat, e) => {
        e.stopPropagation();
        if (onShareChat) {
            await onShareChat(chat);
        }
        setOpenMenuId(null);
    };

    const handleDeleteClick = async (chatId, e) => {
        e.stopPropagation();
        if (window.confirm('Are you sure you want to delete this chat?')) {
            if (onDeleteChat) {
                await onDeleteChat(chatId);
            }
        }
        setOpenMenuId(null);
    };

    return (
        <aside
            className={`
        bg-white dark:bg-gray-900 border-r border-gray-200 dark:border-gray-700 h-full flex flex-col transition-all duration-300 shrink-0
        ${isCollapsed ? 'w-16' : 'w-64'}
      `}
        >
            {/* Header with Logo */}
            <div className="p-4 border-b border-gray-100 dark:border-gray-800 flex items-center justify-between h-16">
                {!isCollapsed ? (
                    <button 
                        onClick={() => onNavigate('home')}
                        className="flex items-center gap-3 hover:opacity-80 transition-opacity"
                    >
                        <img
                            src="/CI-logo.png"
                            alt="CI Logo"
                            className="h-8 w-auto"
                        />
                        <span className="font-semibold text-gray-800 dark:text-gray-200 text-sm whitespace-nowrap">Creyente AI Agents</span>
                    </button>
                ) : (
                    <button 
                        onClick={() => onNavigate('home')}
                        className="hover:opacity-80 transition-opacity"
                        title="Creyente AI Agents"
                    >
                        <img
                            src="/CI-logo.png"
                            alt="CI Logo"
                            className="h-8 w-auto"
                        />
                    </button>
                )}
                <button
                    onClick={onToggleCollapse}
                    className="p-1.5 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-800 text-gray-500 dark:text-gray-400 hover:text-orange-600 dark:hover:text-orange-500 transition-colors ml-auto"
                >
                    <ChevronLeft size={18} className={`transition-transform ${isCollapsed ? 'rotate-180' : ''}`} />
                </button>
            </div>

            {/* Navigation */}
            <nav className="flex-1 overflow-y-auto">
                {/* Menu Items */}
                <div className="p-3 space-y-1">
                    {menuItems.map((item) => {
                        const Icon = item.icon;
                        const isActive = currentAgent === item.id;

                        return (
                            <button
                                key={item.id}
                                onClick={() => {
                                    if (item.id === 'new-chat') {
                                        if (onNewChat) {
                                            onNewChat();
                                        }
                                    } else {
                                        onNavigate(item.id);
                                    }
                                }}
                                className={`
                  w-full flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium transition-all
                  ${isActive
                                        ? 'bg-gray-100 dark:bg-gray-800 text-gray-900 dark:text-white'
                                        : 'text-gray-600 dark:text-gray-400 hover:bg-gray-50 dark:hover:bg-gray-800 hover:text-gray-900 dark:hover:text-white'
                                    }
                  ${isCollapsed ? 'justify-center px-2' : ''}
                `}
                                title={isCollapsed ? item.name : ''}
                            >
                                <Icon size={20} className={isActive ? 'text-gray-900 dark:text-white' : 'text-gray-500 dark:text-gray-400'} />
                                {!isCollapsed && <span>{item.name}</span>}
                            </button>
                        );
                    })}
                </div>

                {/* Chat History Section */}
                {!isCollapsed && chatHistory.length > 0 && (
                    <div className="px-3 mt-6">
                        <div className="mb-2 px-3">
                            <span className="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wide">
                                Recent Chats
                            </span>
                        </div>
                        <div className="space-y-0">
                            {chatHistory.slice(0, 10).map((chat, index) => (
                                <div key={chat.sessionId}>
                                    <div className="relative group">
                                        {renamingChatId === chat.sessionId ? (
                                            // Rename input
                                            <div className="px-3 py-2.5 bg-gray-50 dark:bg-gray-800 rounded-lg" onClick={(e) => e.stopPropagation()}>
                                                <input
                                                    type="text"
                                                    value={renameValue}
                                                    onChange={(e) => setRenameValue(e.target.value)}
                                                    onKeyDown={(e) => {
                                                        if (e.key === 'Enter') handleRenameSubmit(chat.sessionId, e);
                                                        if (e.key === 'Escape') handleRenameCancel(e);
                                                    }}
                                                    onBlur={(e) => handleRenameSubmit(chat.sessionId, e)}
                                                    className="w-full px-2 py-1 text-sm border border-orange-500 rounded focus:outline-none focus:ring-2 focus:ring-orange-500 bg-white dark:bg-gray-900 text-gray-900 dark:text-white"
                                                    autoFocus
                                                />
                                            </div>
                                        ) : (
                                            // Chat item
                                            <button
                                                onClick={() => onLoadChat && onLoadChat(chat.sessionId)}
                                                className="w-full text-left px-3 py-0.5 rounded-lg text-sm text-gray-600 dark:text-gray-400 hover:bg-gray-50 dark:hover:bg-gray-800 hover:text-gray-900 dark:hover:text-white transition-all group"
                                                title={chat.title}
                                            >
                                                <div className="flex items-center justify-between gap-2">
                                                    <div className="flex-1 min-w-0">
                                                        <p className="truncate font-medium group-hover:text-orange-600 dark:group-hover:text-orange-500 text-sm">
                                                            {chat.title}
                                                        </p>
                                                    </div>
                                                    <span className="text-xs text-gray-400 dark:text-gray-500 whitespace-nowrap flex-shrink-0">
                                                        {formatTimestamp(chat.updatedAt)}
                                                    </span>
                                                    
                                                    {/* Three dots menu */}
                                                    <div className="relative flex-shrink-0">
                                                        <button
                                                            onClick={(e) => handleMenuToggle(chat.sessionId, e)}
                                                            className="p-1 rounded hover:bg-gray-200 dark:hover:bg-gray-700 opacity-0 group-hover:opacity-100 transition-opacity"
                                                            title="More options"
                                                        >
                                                            <MoreVertical size={16} className="text-gray-500 dark:text-gray-400" />
                                                        </button>
                                                        
                                                        {/* Dropdown menu */}
                                                        {openMenuId === chat.sessionId && (
                                                            <div className="absolute right-0 top-8 w-48 bg-white dark:bg-gray-800 rounded-lg shadow-lg border border-gray-200 dark:border-gray-700 py-1 z-50">
                                                                <button
                                                                    onClick={(e) => handleShareClick(chat, e)}
                                                                    className="w-full flex items-center gap-3 px-4 py-2 text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
                                                                >
                                                                    <Share size={16} />
                                                                    <span>Share as PDF</span>
                                                                </button>
                                                                <button
                                                                    onClick={(e) => handleRenameClick(chat, e)}
                                                                    className="w-full flex items-center gap-3 px-4 py-2 text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
                                                                >
                                                                    <Edit2 size={16} />
                                                                    <span>Rename</span>
                                                                </button>
                                                                <button
                                                                    onClick={(e) => handleDeleteClick(chat.sessionId, e)}
                                                                    className="w-full flex items-center gap-3 px-4 py-2 text-sm text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/20 transition-colors"
                                                                >
                                                                    <Trash2 size={16} />
                                                                    <span>Delete</span>
                                                                </button>
                                                            </div>
                                                        )}
                                                    </div>
                                                </div>
                                            </button>
                                        )}
                                    </div>
                                    
                                    {/* Thin divider line between chats */}
                                    {index < chatHistory.slice(0, 10).length - 1 && (
                                        <div className="my-1 mx-3 border-t border-gray-100 dark:border-gray-800"></div>
                                    )}
                                </div>
                            ))}
                        </div>
                    </div>
                )}
            </nav>

            {/* User Info & Logout at Bottom */}
            <div className={`border-t border-gray-100 dark:border-gray-800 ${isCollapsed ? 'p-2' : 'p-4'}`}>
                {user && !isCollapsed && (
                    <div className="flex items-center gap-3 mb-4 p-3 rounded-xl bg-orange-50 dark:bg-orange-900/20">
                        {/* Larger Avatar with Green Status Dot */}
                        <div className="relative">
                            <div className="w-12 h-12 rounded-full bg-orange-500 flex items-center justify-center text-white font-bold text-lg shadow-sm">
                                {(user.name || user.email || user.username)?.charAt(0)?.toUpperCase() || 'U'}
                            </div>
                            <div className="absolute bottom-0 right-0 w-3 h-3 bg-green-500 rounded-full border-2 border-white dark:border-gray-900"></div>
                        </div>
                        <div className="flex flex-col">
                            <span className="text-gray-900 dark:text-white font-bold text-sm leading-tight">
                                {user.name || user.email || user.username}
                            </span>
                            <span className="text-xs text-orange-600 dark:text-orange-400 font-medium leading-tight mt-0.5">
                                {currentAgent === 'inventory' ? 'Inventory Agent' : currentAgent === 'recruiter' ? 'Recruiter Agent' : currentAgent === 'feedback' ? 'Feedback Agent' : 'Agent'}
                            </span>
                        </div>
                    </div>
                )}

                {/* Sign Out Button - Flat Orange Style */}
                <button
                    onClick={logout}
                    className={`w-full flex items-center justify-center gap-2 px-4 py-3.5 rounded-xl text-sm font-bold text-white bg-orange-500 hover:bg-orange-600 shadow-md transition-all active:scale-95 ${isCollapsed ? 'p-0 aspect-square' : ''}`}
                    title="Sign Out"
                >
                    <LogOut size={isCollapsed ? 20 : 18} color="white" strokeWidth={2.5} />
                    {!isCollapsed && <span>Sign Out</span>}
                </button>

                {/* Copyright Footer */}
                {!isCollapsed && (
                    <p className="text-center text-[10px] text-gray-400 dark:text-gray-500 font-medium mt-6">
                        © 2026 Creyente InfoTech
                    </p>
                )}
            </div>
        </aside>
    );
};

export default AgentSidebar;

